import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import { Link } from "react-router-dom";
import { ExternalLink, Calendar, ClipboardCheck, PencilLine, Megaphone, ArrowRight } from "lucide-react";
import { ENDORSEMENTS, ENDORSEMENTS_2026, SITE } from "@/data/content";

export default function Endorsements() {
  return (
    <>
      <SEO
        title="Endorsements | Veterans for Indiana"
        description="See the veterans and military family members endorsed by Veterans for Indiana — candidates running with leadership, service, and integrity."
        path="/endorsements"
      />

      {/* HERO with Indiana flag + Statehouse style background */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-35"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(11,42,74,0.95) 0%, rgba(11,42,74,0.55) 60%), url('https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Indiana_Statehouse_-_Indianapolis.jpg/1600px-Indiana_Statehouse_-_Indianapolis.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 pattern-stars opacity-70" />
        <div className="container-x relative py-20 lg:py-28 text-center max-w-3xl mx-auto">
          <Reveal>
            <div className="flex justify-center mb-3"><StarDivider color="#5BA63B" /></div>
            <h1 className="h-display text-5xl sm:text-6xl text-white">Endorsements</h1>
            <p className="mt-6 text-white/90 leading-relaxed">
              Veterans for Indiana proudly supports candidates who demonstrate leadership, character, integrity, and a deep commitment to Hoosier communities.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THREE-COLUMN ENDORSEMENT HUB */}
      <section className="bg-white py-20" aria-labelledby="hub-title">
        <h2 id="hub-title" className="sr-only">Endorsement Hub</h2>
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 2024 (Previous) Endorsements */}
          <Reveal delay={1}>
            <article className="bg-gray-soft rounded-lg p-8 h-full flex flex-col ring-1 ring-gray-200">
              <p className="font-display tracking-[0.22em] text-xs uppercase text-green-brand">2024 Cycle</p>
              <h3 className="font-display tracking-widest text-navy uppercase text-xl mt-1">Previous Endorsements</h3>
              <span className="mt-3 inline-block h-[2px] w-12 bg-green-brand" />
              <p className="mt-4 text-navy-deep/80 leading-relaxed">
                Review our track record from the 2024 cycle — 16 veterans and military family members supported across Indiana.
              </p>

              <a
                href="#current-grid"
                className="mt-6 flex items-center justify-between gap-3 bg-white rounded-md p-4 ring-1 ring-gray-200 hover:ring-green-brand hover:shadow-md transition-all"
                data-testid="previous-2024-card"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-brand/10 text-green-brand">
                    <Calendar size={18} />
                  </span>
                  <span className="font-display tracking-wide text-navy uppercase">2024 Endorsements</span>
                </span>
                <ArrowRight className="text-green-brand" size={20} />
              </a>

              <a
                href="#current-grid"
                className="btn btn-primary w-full mt-6"
                data-testid="view-all-endorsements"
              >
                View 2024 Endorsements
              </a>
            </article>
          </Reveal>

          {/* Apply for Endorsement — dark navy center column */}
          <Reveal delay={2}>
            <article className="relative bg-navy-deep text-white rounded-lg p-8 h-full flex flex-col overflow-hidden ring-1 ring-navy-deep">
              <div className="absolute inset-0 pattern-stars opacity-80" />
              <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-brand text-white">
                  <ClipboardCheck size={28} />
                </div>
                <h3 className="font-display tracking-widest uppercase text-xl mt-5 text-white">Apply for Endorsement</h3>
                <div className="mt-3"><StarDivider color="#5BA63B" inline /></div>
                <p className="mt-4 text-white/85 leading-relaxed">
                  Veteran or military family member running for office in Indiana? Complete the Veterans for Indiana PAC Endorsement Application to be considered.
                </p>
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfS1SlR6ouD7H6YhSv-5zdpdPZPZMktnBq6XrpTnRCywybKpQ/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary mt-6 w-full"
                  data-testid="apply-endorsement-cta"
                >
                  <PencilLine size={18} /> Start Your Application <ExternalLink size={16} />
                </a>
                <Link
                  to="/what-we-do"
                  className="mt-4 inline-block text-sm text-white/80 hover:text-green-brand underline underline-offset-4"
                  data-testid="learn-process-link"
                >
                  Learn more about our endorsement process
                </Link>
              </div>
            </article>
          </Reveal>

          {/* Current Endorsements — 2026 cycle (live) */}
          <Reveal delay={3}>
            <article className="bg-gray-soft rounded-lg p-8 h-full flex flex-col ring-1 ring-gray-200">
              <p className="font-display tracking-[0.22em] text-xs uppercase text-green-brand">2026 Cycle</p>
              <h3 className="font-display tracking-widest text-navy uppercase text-xl mt-1">Current Endorsements</h3>
              <span className="mt-3 inline-block h-[2px] w-12 bg-green-brand" />
              <p className="mt-4 text-navy-deep/80 leading-relaxed">
                Meet the {ENDORSEMENTS_2026.length} veterans and military family members we&rsquo;re proud to endorse this cycle — from statewide office to local township boards.
              </p>

              <a
                href="#current-2026"
                className="mt-6 flex items-center justify-between gap-3 bg-white rounded-md p-4 ring-1 ring-gray-200 hover:ring-green-brand hover:shadow-md transition-all"
                data-testid="current-2026-card"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-brand/10 text-green-brand">
                    <Calendar size={18} />
                  </span>
                  <span className="font-display tracking-wide text-navy uppercase">2026 Endorsements</span>
                </span>
                <ArrowRight className="text-green-brand" size={20} />
              </a>

              <a
                href="#current-2026"
                className="btn btn-primary w-full mt-6"
                data-testid="view-all-current"
              >
                View 2026 Endorsements
              </a>
            </article>
          </Reveal>
        </div>
      </section>

      {/* 2026 ENDORSEMENTS GRID */}
      <section id="current-2026" className="bg-white py-20 scroll-mt-24" aria-labelledby="grid-2026-title">
        <div className="container-x">
          <Reveal className="text-center">
            <p className="font-display tracking-[0.22em] text-xs uppercase text-green-brand">2026 Cycle</p>
            <h2 id="grid-2026-title" className="h-display text-3xl sm:text-4xl text-navy mt-1">2026 Endorsements</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
            <p className="mt-4 text-navy-deep/80 max-w-2xl mx-auto">
              {ENDORSEMENTS_2026.length} veterans and military family members proudly endorsed by Veterans for Indiana in the 2026 election cycle.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7">
            {ENDORSEMENTS_2026.map((c, i) => (
              <Reveal key={c.name + c.office} delay={((i % 4) + 1)}>
                <article className="card-lift bg-white rounded-lg overflow-hidden shadow-sm ring-1 ring-gray-200 h-full flex flex-col">
                  <div className="aspect-square bg-gray-soft overflow-hidden relative">
                    <img
                      src={c.photo}
                      alt={`Veterans for Indiana 2026 endorsed candidate ${c.name} for ${c.office}`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display text-xl text-navy uppercase tracking-wide">{c.name}</h3>
                    <p className="mt-1 text-sm text-navy-deep/70">{c.office}</p>
                    <span className="mt-3 inline-block h-[2px] w-10 bg-green-brand" />
                    {c.link && (
                      <div className="mt-4">
                        <a
                          href={c.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-display tracking-widest text-xs uppercase text-green-brand hover:text-navy inline-flex items-center gap-1"
                          data-testid={`endorse-2026-learn-more-${i}`}
                        >
                          {c.link.includes("facebook.com") ? "Follow on Facebook" : "Visit Website"} <ExternalLink size={14} />
                        </a>
                      </div>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section id="current-grid" className="bg-cream py-20 scroll-mt-24" aria-labelledby="grid-title">
        <div className="container-x">
          <Reveal className="text-center">
            <h2 id="grid-title" className="h-display text-3xl sm:text-4xl text-navy">2024 Endorsements</h2>
            <div className="mt-3 flex justify-center"><StarDivider /></div>
            <p className="mt-4 text-navy-deep/80 max-w-2xl mx-auto">
              {ENDORSEMENTS.length} veteran and military family candidates we supported across Indiana in the 2024 election cycle.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7">
            {ENDORSEMENTS.map((c, i) => (
              <Reveal key={c.name + c.office} delay={((i % 4) + 1)}>
                <article className="card-lift bg-white rounded-lg overflow-hidden shadow-sm ring-1 ring-gray-200 h-full flex flex-col">
                  <div className="aspect-[4/3] bg-gray-soft overflow-hidden relative">
                    <img
                      src={c.photo}
                      alt={`Veterans for Indiana endorsed candidate ${c.name}`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 bg-green-brand text-white font-display tracking-widest text-[10px] uppercase px-2 py-1 rounded">
                      Endorsed
                    </span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display text-xl text-navy uppercase tracking-wide">{c.name}</h3>
                    <p className="mt-1 text-sm text-navy-deep/70">{c.office}</p>
                    <span className="mt-3 inline-block h-[2px] w-10 bg-green-brand" />
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-display tracking-widest text-xs uppercase text-green-brand hover:text-navy inline-flex items-center gap-1"
                        data-testid={`endorse-learn-more-${i}`}
                      >
                        Learn More <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP / GET INVOLVED dark navy band — matches image 3 */}
      <section className="relative bg-navy-deep text-white overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-80" />
        <div className="container-x relative py-16 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="flex items-start gap-5">
            <Megaphone className="text-white shrink-0 mt-1" size={48} aria-hidden="true" />
            <div>
              <h2 className="h-display text-3xl sm:text-4xl text-white">Leadership Starts with Service.</h2>
              <p className="mt-3 text-white/85 max-w-xl">
                Help us elect more veterans and military families to build a stronger Indiana.
              </p>
            </div>
          </Reveal>
          <Reveal delay={1} className="lg:justify-self-end flex flex-wrap gap-3">
            <Link to="/get-involved" className="btn btn-primary" data-testid="endorse-get-involved-cta">Get Involved</Link>
            <a href={SITE.donateUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-light" data-testid="endorse-donate-cta">Donate</a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
